import { INFINITY } from '@/features/referral/constants';
import { useUnmount } from 'ahooks';
import { useState, useCallback, useRef, useMemo } from 'react';

interface CountdownResult {
  displayText: string;
  displaySeconds: number;
  formattedRes: { 
    days: number; hours: number; hoursPad0: string; minutes: number; minutesPad0: string; seconds: number; secondsPad0: string;
  } | null;
  isExpired: boolean;
  isInfinite: boolean;
  startCountDown: (startSeconds: number, onEnd?: () => any) => void;
  stopCountDown: () => void;
}

/**
 * 倒计时Hook（基于目标时间戳的精确倒计时）
 * @returns 倒计时显示文本，显示规则为：大于1天，显示天时分，每分钟更新一次；小于1天，显示时分秒，每秒更新一次，无穷则不显示
 *
 * 核心改进：
 * - 存储目标结束时间戳，而非剩余秒数
 * - 每次更新时基于当前真实时间计算剩余时间
 * - 消除 setInterval 不精确导致的累积误差
 * - 自动校准后台/锁屏导致的时间漂移
 */
export const useCountdown = (params: { size: 'min' | 'max'; showSecondOverDay?: boolean} = { size: 'max', showSecondOverDay: false  }): CountdownResult => {
  const [endTimestamp, setEndTimestamp] = useState<number | undefined>(undefined); // 目标结束时间戳（秒）
  const [displaySeconds, setDisplaySeconds] = useState<number>(0); // 用于显示的剩余秒数
  const [isExpired, setIsExpired] = useState(false);
  // 只有明确设置为 INFINITY 时才认为是无穷倒计时（排除 undefined 初始状态）
  const isInfinite = useMemo(() => endTimestamp !== undefined && endTimestamp === INFINITY, [endTimestamp]);
  const timerRef = useRef<NodeJS.Timeout>(null);

  // 格式化时间显示
  const formatTimeStr = useCallback((totalSeconds: number): string => {
    if (totalSeconds <= 0) return '00:00:00';

    const days = Math.floor(totalSeconds / 86400); // 1天 = 86400秒
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    const hourStr = hours.toString().padStart(2, '0');
    const minStr = minutes.toString().padStart(2, '0');
    const secStr = remainingSeconds.toString().padStart(2, '0');
    // 大于1天，显示天时分
    if (days > 0) {
      if (params.showSecondOverDay) {
        return `${days}d ${hourStr}:${minStr}:${secStr}`;
      } else {
        return `${days}d ${hourStr}h ${minStr}m`;
      }
    }

    // 小于1天，显示时分秒
    return params.size === 'max' ?
      `${hourStr} : ${minStr} : ${secStr}`:
      `${hourStr}:${minStr}:${secStr}`
  }, []);

  const formatTime = useCallback((totalSeconds: number): CountdownResult['formattedRes'] => {
    if (totalSeconds <= 0) return {
      days: 0,
      hours: 0,
      hoursPad0: '',
      minutes: 0,
      minutesPad0: '0',
      seconds: 0,
      secondsPad0: '0',
    };

    const days = Math.floor(totalSeconds / 86400); // 1天 = 86400秒
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const remainingSeconds = totalSeconds % 60;

    const hourStr = hours.toString().padStart(2, '0');
    const minStr = minutes.toString().padStart(2, '0');
    const secStr = remainingSeconds.toString().padStart(2, '0');

    return {
      days,
      hours,
      hoursPad0: hourStr,
      minutes,
      minutesPad0: minStr,
      seconds: remainingSeconds,
      secondsPad0: secStr,
    }
  }, []);

  // 更新倒计时
  useUnmount(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current as NodeJS.Timeout);
      timerRef.current = null;
    }
  });

  const startCountDown = (startSeconds: number, onEnd?: () => any) => {
    // 处理无穷倒计时（INFINITY = -1）
    if (startSeconds === INFINITY) {
      setEndTimestamp(INFINITY);
      setDisplaySeconds(0);
      setIsExpired(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // 计算目标结束时间戳（当前时间 + 剩余秒数）
    const targetEndTimestamp = Math.floor(Date.now() / 1000) + startSeconds;
    setEndTimestamp(targetEndTimestamp);
    setDisplaySeconds(startSeconds);
    setIsExpired(false);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // 大于1天时，每分钟更新一次；否则每秒更新
    const updateInterval = startSeconds > 86400 && !params.showSecondOverDay ? 60000 : 1000; // 60000ms = 1分钟, 1000ms = 1秒

    timerRef.current = setInterval(() => {
      // 基于当前真实时间计算剩余秒数（核心改进）
      const now = Math.floor(Date.now() / 1000);
      const remaining = targetEndTimestamp - now;

      if (remaining <= 0) {
        setDisplaySeconds(0);
        setIsExpired(true);
        if (timerRef.current) {
          clearInterval(timerRef.current as NodeJS.Timeout);
          timerRef.current = null;
        }
        onEnd?.();
      } else {
        setDisplaySeconds(remaining);
      }
    }, updateInterval);
  }

  // 停止倒计时
  const stopCountDown = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setEndTimestamp(undefined);
    setDisplaySeconds(0);
    setIsExpired(false);
  }, []);

  return {
    displayText: isInfinite || !displaySeconds ? '' : formatTimeStr(displaySeconds),
    formattedRes: isInfinite || !displaySeconds ? null : formatTime(displaySeconds),
    displaySeconds,
    isExpired,
    isInfinite,
    startCountDown,
    stopCountDown,
  };
};
